import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Typography, Box } from '@mui/material';
import { Alert, Card, CardBody, Spinner } from 'reactstrap';
import Breadcrumbs from '../../shims/commonUiBreadcrumbs';
import Btn from '../../shims/commonUiBtn';
import FileAttachmentZone from './FileAttachmentZone';
import { createTicket } from '../../api/ticketApi';

const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 5000;

export default function CreateTicketForm() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successTicketId, setSuccessTicketId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [attachmentWarnings, setAttachmentWarnings] = useState([]);

  const validate = () => {
    const next = {};
    if (!subject?.trim()) next.subject = 'Subject is required.';
    if (!description?.trim()) next.description = 'Description is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFilesChange = (newFiles, err) => {
    setFiles(newFiles);
    setFileError(err);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setAttachmentWarnings([]);
    if (fileError) return;
    if (!validate()) return;
    setIsLoading(true);
    setUploadProgress(0);
    try {
      const res = await createTicket(
        { subject: subject.trim(), description: description.trim(), files },
        { onProgress: setUploadProgress }
      );
      setUploadProgress(100);
      const ticketId = res?.ticketId;
      const warnings = res?.attachmentWarnings;
      if (warnings?.length) setAttachmentWarnings(warnings);
      setSuccessTicketId(ticketId);
      setTimeout(() => {
        navigate(`/ticketing/tickets/${ticketId}`);
      }, warnings?.length ? 2500 : 1500);
    } catch (err) {
      const msg = err?.json?.message || err?.json?.error || 'Failed to submit ticket. Please try again.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  };

  const handleCancel = () => navigate('/ticketing/tickets');

  return (
    <>
      <div className="d-flex mtop-1 flex-wrap align-items-center justify-content-between gap-2">
        <Breadcrumbs mainTitle="Create ticket" />
      </div>
      <Card className="mx-auto" style={{ maxWidth: 640 }}>
        <CardBody>
          {successTicketId && (
            <Alert color="success" toggle={() => setSuccessTicketId(null)} className="mb-2">
              Ticket {successTicketId} created successfully.
            </Alert>
          )}
          {errorMessage && (
            <Alert color="danger" role="alert" toggle={() => setErrorMessage(null)} className="mb-2">
              {errorMessage}
            </Alert>
          )}
          {attachmentWarnings.length > 0 && (
            <Alert color="warning" toggle={() => setAttachmentWarnings([])} className="mb-2">
              Ticket created but some files could not be attached: {attachmentWarnings.join(' ')}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
              inputProps={{ maxLength: SUBJECT_MAX }}
              error={Boolean(errors.subject)}
              helperText={errors.subject}
              disabled={isLoading}
              margin="normal"
              required
              aria-describedby={errors.subject ? 'subject-error' : 'subject-remaining'}
            />
            <Typography id="subject-remaining" variant="caption" sx={{ display: 'block', mt: -0.5 }} aria-live="polite">
              {SUBJECT_MAX - subject.length} characters remaining
            </Typography>
            {errors.subject && (
              <Typography id="subject-error" variant="caption" color="error" role="alert">
                {errors.subject}
              </Typography>
            )}

            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
              inputProps={{ maxLength: DESCRIPTION_MAX }}
              error={Boolean(errors.description)}
              helperText={errors.description}
              disabled={isLoading}
              margin="normal"
              multiline
              minRows={4}
              required
              aria-label="Description"
              aria-describedby={errors.description ? 'description-error' : 'description-remaining'}
            />
            <Typography id="description-remaining" variant="caption" sx={{ display: 'block', mt: -0.5 }} aria-live="polite">
              {DESCRIPTION_MAX - description.length} characters remaining
            </Typography>
            {errors.description && (
              <Typography id="description-error" variant="caption" color="error" role="alert">
                {errors.description}
              </Typography>
            )}

            <FileAttachmentZone
              files={files}
              onChange={handleFilesChange}
              error={fileError}
              disabled={isLoading}
              uploadProgress={uploadProgress}
            />

            <Box className="d-flex gap-2 mt-3">
              <Btn attrBtn={{ type: 'submit', disabled: isLoading, color: 'primary' }}>
                {isLoading ? <Spinner size="sm" color="light" /> : 'Submit'}
              </Btn>
              <Btn attrBtn={{ type: 'button', color: 'secondary', onClick: handleCancel, disabled: isLoading }}>
                Cancel
              </Btn>
            </Box>
          </form>
        </CardBody>
      </Card>
    </>
  );
}
